import { Component, OnInit, signal, inject, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Subject, takeUntil, debounceTime } from 'rxjs';

import { PurchaseRequestService } from '../purchase-request.service';
import { PurchaseRequestPurpose } from '../purchase-request.config';
import {
  AddFormComponent,
  FormField,
  TableSection,
  ActionButton,
} from '@shared/components/add-form/add-form.component';
import { PurchaseRequestItem } from '../models/purchase-request-item';
import { PurchaseRequestItemDialogComponent } from '../../components/selected-sku-dialog/selected-sku-dialog.component';
import { WarehouseOptionsService } from '@shared/services/warehouse-options.service';
import { PurchaseRequestDataService } from '../services/purchase-request-data.service';
import { ErrorTranslatorService } from '@app/shared/services/error-translator-service';
import { ErrorDialogService } from '@app/shared/services/error-dialog.service';
import { CanComponentDeactivate } from '@app/core/auth/can-component-deactivate.interface';

@Component({
  selector: 'app-purchase-request-add',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    AddFormComponent,
    PurchaseRequestItemDialogComponent,
    ToastModule,
  ],
  providers: [MessageService],
  templateUrl: './purchase-request-add.component.html',
  styleUrl: './purchase-request-add.component.css',
})
export class PurchaseRequestAddComponent implements OnInit, OnDestroy, CanComponentDeactivate {
  @ViewChild('addForm') private readonly addFormComponent?: AddFormComponent;

  private readonly router = inject(Router);
  private readonly messageService = inject(MessageService);
  private readonly purchaseRequestService = inject(PurchaseRequestService);
  private readonly translateService = inject(TranslateService);
  private errorDialogService = inject(ErrorDialogService);
  private errorTranslatorService = inject(ErrorTranslatorService);

  private readonly warehouseOptionsService = inject(WarehouseOptionsService);
  private readonly purchaseRequestDataService = inject(
    PurchaseRequestDataService
  );
  private readonly destroy$ = new Subject<void>();
  private readonly cellValueChange$ = new Subject<{
    rowData: any;
    prop: string;
    value: any;
  }>();

  // Form configuration
  formFields = signal<FormField[]>([]);
  tableSections = signal<TableSection[]>([]);
  headerActions = signal<ActionButton[]>([]);

  // Data
  itemsList = signal<PurchaseRequestItem[]>([]);
  selectedItems = signal<PurchaseRequestItem[]>([]);
  saving = signal(false);
  currentFormData: any = null;
  warehouseOptions = signal<{ label: string; value: string }[]>([]);
  private skipCanDeactivate = false;
  private readonly showRequiredValidationErrors = signal(false);

  // Dialog states
  showItemDialog = signal(false);
  editingItem = signal<PurchaseRequestItem | null>(null);

  ngOnInit(): void {
    this.initializeConfigurations();
    this.loadOptions();
    this.loadWarehouses();
    this.updateItemsTableData();

    // Set up debounced cell value changes to prevent input focus loss
    this.cellValueChange$
      .pipe(
        debounceTime(300), // Wait 300ms after user stops typing
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        this.updateItemsListFromCellChange(event);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onFormAction(action: string): void {
    switch (action) {
      case 'save':
        this.onSave();
        break;
      case 'cancel':
        this.onCancel();
        break;
    }
  }

  onTableAction(event: { action: string; data?: any; section?: string }): void {
    switch (event.action) {
      case 'addItem':
        this.onAddItem();
        break;
      case 'editItem':
        this.onEditItem(event.data);
        break;
      case 'deleteItem':
        this.onDeleteItem(event.data);
        break;
      case 'deleteSelectedItems':
        this.onDeleteSelectedItems();
        break;
      case 'selectionChange':
        this.onSelectionChange(event.section, event.data);
        break;
      case 'cellEdit':
        this.onCellValueChange(event.data);
        break;
      case 'cellValueChange':
        this.onCellValueChange(event.data);
        break;
      default:
        break;
    }
  }

  onFormValueChanges(formData: any): void {
    // Store the current form data
    this.currentFormData = formData;
  }

  private onSave(): void {
    this.showRequiredValidationErrors.set(true);
    this.updateItemsTableData();

    if (!this.currentFormData) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_request.messages.please_fill_required_fields'
        ),
      });
      return;
    }

    if (this.hasMissingRequiredFormData()) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_request.messages.please_fill_required_fields'
        ),
      });
      return;
    }

    if (this.itemsList().length === 0) {
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(
          'purchase_request.messages.please_add_at_least_one_item'
        ),
      });
      return;
    }

    const validationErrors = this.validateItems();
    if (validationErrors.length > 0) {
      const messageKey = validationErrors.includes('missing_warehouse')
        ? 'purchase_request.messages.please_fill_required_fields'
        : 'purchase_request.messages.duplicate_sku_warehouse_combination';
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.messages.error'),
        detail: this.translateService.instant(messageKey),
      });
      return;
    }

    this.saving.set(true);

    // Create purchase request data for API using PurchaseRequestDTO structure
    const items = this.itemsList().map((item) => {
      return {
        skuCode: item.skuCode,
        scheduleDate: undefined, // Set to undefined since schedule date is removed from UI
        quantity: item.quantity,
        warehouseCode: item.warehouseCode || '',
      };
    });

    // Create the data structure matching your DTO
    const purchaseRequestData = {
      title: this.currentFormData.title,
      series: this.currentFormData.series,
      scheduleDate: this.currentFormData.scheduleDate,
      items: items,
    };

    // Call the real API
    this.purchaseRequestService
      .createPurchaseRequest(purchaseRequestData)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.saving.set(false);
          this.skipCanDeactivate = true;
          this.addFormComponent?.form?.markAsPristine();
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.messages.success'),
            detail: this.translateService.instant(
              'purchase_request.messages.created_successfully'
            ),
          });
          // Navigate back to the list page
          setTimeout(() => {
            this.router.navigate(['/procurement/purchase-request']);
          }, 1000);
        },
        error: (error) => {
          this.saving.set(false);
          this.handleApiError(error);
        },
      });
  }

  private onCancel(): void {
    this.router.navigate(['/procurement/purchase-request']);
  }

  canDeactivate(): boolean {
    if (this.skipCanDeactivate || this.saving()) {
      return true;
    }

    return !(this.addFormComponent?.form?.dirty ?? false);
  }

  private initializeConfigurations(): void {
    // Initialize form fields with translated labels
    this.formFields.set([
      // Basic Information
      {
        label: this.translateService.instant('purchase_request.fields.series'),
        controlName: 'series',
        type: 'text',
        placeholder: this.translateService.instant(
          'purchase_request.placeholders.enter_series'
        ),
        section: this.translateService.instant(
          'purchase_request.sections.basic_information'
        ),
        required: true,
        maxLength: 50,
      },
      {
        label: this.translateService.instant('purchase_request.fields.title'),
        controlName: 'title',
        type: 'text',
        placeholder: this.translateService.instant(
          'purchase_request.placeholders.enter_title'
        ),
        section: this.translateService.instant(
          'purchase_request.sections.basic_information'
        ),
        required: true,
        maxLength: 200,
      },
      {
        label: this.translateService.instant(
          'purchase_request.fields.schedule_date'
        ),
        controlName: 'scheduleDate',
        type: 'date',
        placeholder: this.translateService.instant(
          'purchase_request.placeholders.select_schedule_date'
        ),
        section: this.translateService.instant(
          'purchase_request.sections.basic_information'
        ),
        required: true,
        minDate: new Date(), // Disable past dates
      },
    ]);

    // Initialize table sections
    this.tableSections.set([
      {
        testIdKey: 'purchase_request.sections.item_details',
        title: this.translateService.instant(
          'purchase_request.sections.item_details'
        ),
        show: true,
        columns: [
          {
            label: this.translateService.instant(
              'purchase_request.fields.spu_code'
            ),
            prop: 'spuCode',
            width: 150,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.spu_name'
            ),
            prop: 'spuName',
            width: 200,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.sku_code'
            ),
            prop: 'skuCode',
            width: 150,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.sku_name'
            ),
            prop: 'skuName',
            width: 200,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.quantity'
            ),
            prop: 'quantity',
            width: 160,
            type: 'inputNumber',
            useGrouping: true,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.warehouse_code'
            ),
            prop: 'warehouseCode',
            width: 150,
            type: 'select',
            options: this.warehouseOptions(),
            required: true,
            placeholder: this.translateService.instant(
              'purchase_request.placeholders.select_warehouse'
            ),
            filter: true,
          },
          {
            label: this.translateService.instant(
              'purchase_request.fields.actions'
            ),
            prop: 'actions',
            type: 'actions',
            width: 80,
          },
        ],
        data: [],
        actions: [
          {
            label: this.translateService.instant(
              'purchase_request.actions.delete'
            ),
            action: 'deleteSelectedItems',
            severity: 'danger',
            outlined: true,
            disabled: true,
            icon: 'delete',
          },
          {
            label: this.translateService.instant(
              'purchase_request.actions.add_item'
            ),
            action: 'addItem',
            severity: 'info',
            icon: 'add',
          },
        ],
        hasCheckbox: true,
      },
    ]);

    // Initialize header actions with translated labels
    this.headerActions.set([
      {
        label: this.translateService.instant('purchase_request.actions.save'),
        action: 'save',
        severity: 'info' as const,
      },
      {
        label: this.translateService.instant('purchase_request.actions.cancel'),
        action: 'cancel',
        outlined: true,
        severity: 'secondary' as const,
      },
    ]);
  }

  private loadOptions(): void {
    // Load purpose options with translated labels
    this.formFields.set(
      this.formFields().map((field) =>
        field.controlName === 'purpose'
          ? { ...field, options: this.getPurposeOptions() }
          : field
      )
    );
  }

  private getPurposeOptions(): any[] {
    return [
      {
        label: this.translateService.instant(
          'purchase_request.purpose.purchase'
        ),
        value: PurchaseRequestPurpose.PURCHASE,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.material_transfer'
        ),
        value: PurchaseRequestPurpose.MATERIAL_TRANSFER,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.material_issue'
        ),
        value: PurchaseRequestPurpose.MATERIAL_ISSUE,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.manufacture'
        ),
        value: PurchaseRequestPurpose.MANUFACTURE,
      },
      {
        label: this.translateService.instant(
          'purchase_request.purpose.customer_provided'
        ),
        value: PurchaseRequestPurpose.CUSTOMER_PROVIDED,
      },
    ];
  }

  private loadWarehouses(): void {
    this.warehouseOptionsService
      .getOptionsPage(
        { page: 1, size: 100, searchText: '' },
        { status: true, valueKey: 'code' },
      )
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (page) => {
          this.warehouseOptions.set(
            page.options.map((opt) => ({
              label: opt.label,
              value: String(opt.value),
            })),
          );
          this.updateWarehouseColumnOptions();
        },
        error: (error) => {
          console.error('Failed to load warehouses:', error);
          this.messageService.add({
            severity: 'error',
            summary: this.translateService.instant('common.messages.error'),
            detail: this.translateService.instant(
              'purchase_request.messages.failed_to_load_warehouses',
            ),
          });
        },
      });
  }

  private updateWarehouseColumnOptions(): void {
    const currentSections = this.tableSections();
    const warehouseOptions = [...this.warehouseOptions()]; // Create new array reference
    const updatedSections = currentSections.map((section) => {
      if (!section.columns) {
        return section;
      }
      const updatedColumns = section.columns.map((col) => {
        if (col.prop === 'warehouseCode') {
          // Create a completely new column object to trigger change detection
          return {
            ...col,
            options: warehouseOptions, // Use new array reference
          };
        }
        return col;
      });
      // Create a completely new section object to trigger change detection
      return {
        ...section,
        columns: [...updatedColumns],
      };
    });
    this.tableSections.set([...updatedSections]);

    // Load selected stock items after warehouses are loaded
    this.loadSelectedStockItems();
  }

  /**
   * Load selected stock items from shared service and convert to purchase request items
   * The StockItem includes skuName from API response even though it's not displayed in stock inquiry table
   */
  private loadSelectedStockItems(): void {
    const selectedStockItems =
      this.purchaseRequestDataService.getAndClearSelectedStockItems();

    if (!selectedStockItems || selectedStockItems.length === 0) {
      return;
    }

    // Convert StockItem to PurchaseRequestItem
    // StockItem already contains skuName from API response
    const purchaseRequestItems: PurchaseRequestItem[] = selectedStockItems.map(
      (stockItem) => ({
        id: this.generateItemId(stockItem.skuCode, stockItem.warehouseCode),
        spuCode: stockItem.spuCode || '',
        spuName: stockItem.spuName || '',
        skuCode: stockItem.skuCode || '',
        skuName: stockItem.skuName || '', // SKU name from API response
        quantity: 1, // Default quantity
        warehouseCode: stockItem.warehouseCode || '',
      })
    );

    // Add items to the list
    this.itemsList.set([...this.itemsList(), ...purchaseRequestItems]);

    // Show success message
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.messages.success'),
      detail: this.translateService.instant(
        'purchase_request.messages.items_added_from_stock_search',
        { count: purchaseRequestItems.length }
      ),
    });

    // Update table data
    this.updateItemsTableData();
  }

  /**
   * Generate a unique ID for the purchase request item
   */
  private generateItemId(skuCode: string, warehouseCode: string): string {
    const timestamp = Date.now();
    const random = Math.floor(Math.random() * 1000);
    return `${skuCode}-${warehouseCode}-${timestamp}-${random}`;
  }

  // Item management methods
  private onAddItem(): void {
    this.editingItem.set(null);
    this.showItemDialog.set(true);
  }

  private onEditItem(data: PurchaseRequestItem): void {
    this.editingItem.set(data);
    this.showItemDialog.set(true);
  }

  private onDeleteItem(data: PurchaseRequestItem): void {
    const updatedList = this.itemsList().filter((item) => item.id !== data.id);
    this.itemsList.set(updatedList);
    this.updateItemsTableData();
  }

  private onDeleteSelectedItems(): void {
    const selectedItems = this.selectedItems();
    if (selectedItems.length === 0) return;

    const selectedIds = selectedItems.map((item) => item.id);
    const updatedList = this.itemsList().filter(
      (item) => !selectedIds.includes(item.id)
    );
    this.itemsList.set(updatedList);
    this.selectedItems.set([]);
    this.updateItemsTableData();
  }

  onSelectionChange(section: string | undefined, selectedItems: any[]): void {
    if (
      section ===
      this.translateService.instant('purchase_request.sections.item_details')
    ) {
      this.selectedItems.set(selectedItems);
      this.updateItemsTableData();
    }
  }

  onCellValueChange(event: { rowData: any; prop: string; value: any }): void {
    // Emit the change to the debounced subject instead of updating immediately
    // This prevents the input field from losing focus on every keystroke
    this.cellValueChange$.next(event);
  }

  private updateItemsListFromCellChange(event: {
    rowData: any;
    prop: string;
    value: any;
  }): void {
    // Update the itemsList signal with the new value (debounced)
    const updatedItemsList = this.itemsList().map((item) => {
      if (item.id === event.rowData.id) {
        let updatedValue = event.value;

        // Handle special cases for different field types
        if (event.prop === 'quantity') {
          // Ensure quantity is a number
          updatedValue = event.value || 0;
        }

        const updatedItem = { ...item, [event.prop]: updatedValue };
        return updatedItem;
      }
      return item;
    });

    this.itemsList.set(updatedItemsList);

    // Update table data to refresh validation errors
    this.updateItemsTableData();
  }

  onItemDialogConfirm(item: PurchaseRequestItem): void {
    if (this.editingItem()) {
      // Update existing item
      const updatedList = this.itemsList().map((existingItem) =>
        existingItem.id === item.id ? item : existingItem
      );
      this.itemsList.set(updatedList);
    } else {
      // Add new item - check if item already exists to avoid duplicates
      const existingItem = this.itemsList().find(
        (existing) => existing.id === item.id
      );
      if (!existingItem) {
        const newList = [...this.itemsList(), item];
        this.itemsList.set(newList);
      }
    }
    this.updateItemsTableData();
  }

  private updateItemsTableData(): void {
    const currentSections = this.tableSections();
    const warehouseOptions = [...this.warehouseOptions()]; // Create new array reference
    const updatedSections = currentSections.map((section) => {
      // Check if this is the item details section by looking for the SPU code column
      const hasSpuCodeColumn = section.columns?.some(
        (col) => col.prop === 'spuCode'
      );
      if (hasSpuCodeColumn && section.columns) {
        const updatedColumns = section.columns.map((col) => {
          if (col.prop === 'warehouseCode') {
            // Create a completely new column object to trigger change detection
            return {
              ...col,
              options: warehouseOptions, // Use new array reference
            };
          }
          return col;
        });
        return {
          ...section,
          columns: [...updatedColumns],
          data: [...this.itemsList()], // Create new array reference
          selection: [...this.selectedItems()], // Create new array reference
          actions: section.actions?.map((action) => {
            if (action.action === 'deleteSelectedItems') {
              return {
                ...action,
                disabled: this.selectedItems().length === 0,
              };
            }
            return action;
          }),
        };
      }
      return section;
    });
    this.tableSections.set([...updatedSections]);
  }

  /**
   * Validate required item data and duplicate SKU + warehouse combinations
   * Returns array of error messages
   */
  private validateItems(): string[] {
    const errors: string[] = [];
    const items = this.itemsList();
    const skuWarehouseMap = new Map<string, number[]>();

    items.forEach((item, index) => {
      if (!item.warehouseCode) {
        errors.push('missing_warehouse');
      }

      if (item.skuCode && item.warehouseCode) {
        const key = `${item.skuCode}_${item.warehouseCode}`;
        if (!skuWarehouseMap.has(key)) {
          skuWarehouseMap.set(key, []);
        }
        skuWarehouseMap.get(key)!.push(index);
      }
    });

    // Check for duplicates
    skuWarehouseMap.forEach((indices, key) => {
      if (indices.length > 1) {
        errors.push(key);
      }
    });

    return errors;
  }

  private hasMissingRequiredFormData(): boolean {
    return !this.currentFormData?.series
      || !this.currentFormData?.title
      || !this.currentFormData?.scheduleDate;
  }

  /**
   * Get field error for table cell validation
   * Used by add-form component to display validation errors
   */
  getFieldError = (rowIndex: number, fieldName: string): string | null => {
    if (fieldName !== 'warehouseCode') {
      return null;
    }

    const items = this.itemsList();
    if (rowIndex < 0 || rowIndex >= items.length) {
      return null;
    }

    const currentItem = items[rowIndex];
    if (!currentItem.warehouseCode && this.showRequiredValidationErrors()) {
      return this.translateService.instant(
        'purchase_request.messages.please_fill_required_fields'
      );
    }

    if (!currentItem.skuCode) {
      return null;
    }

    // Check if this SKU + warehouse combination exists in other rows
    const duplicateCount = items.filter(
      (item, index) =>
        index !== rowIndex &&
        item.skuCode === currentItem.skuCode &&
        item.warehouseCode === currentItem.warehouseCode &&
        item.warehouseCode // Ensure warehouse code is not empty
    ).length;

    if (duplicateCount > 0) {
      // Translate the error message before returning
      return this.translateService.instant(
        'purchase_request.validation.duplicate_sku_warehouse'
      );
    }

    return null;
  };

  private handleApiError(error: any) {
    const fallbackKey = 'purchase_request.messages.failed_to_create';
    this.errorDialogService.showError(this.errorTranslatorService.translate(error.error?.message, fallbackKey));
  }
}
